import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { useAuth } from "../../hooks/useAuth";

// #29/#77 — the agent home page: a Material UI tile grid that launches the
// flows and pages the agent can use.
const TILES = [
  {
    key: "carrier",
    title: "Carrier Request",
    description: "Request a new carrier appointment.",
    Icon: AssignmentOutlinedIcon,
    to: "/contract",
  },
  {
    key: "add-state",
    title: "Add State",
    description: "Add a state to your carrier appointments.",
    Icon: AddLocationAltOutlinedIcon,
    to: "/add-state",
  },
  {
    key: "requests",
    title: "My Requests",
    description: "Track the status of your past requests.",
    Icon: HistoryOutlinedIcon,
    to: "/my-requests",
  },
  {
    key: "profile",
    title: "My Profile",
    description: "Review your details on file.",
    Icon: PersonOutlineOutlinedIcon,
    to: "/my-profile",
  },
];

const TileBody = ({ tile }) => (
  <CardContent sx={{ textAlign: "center", py: 4 }}>
    <tile.Icon color="primary" sx={{ fontSize: 48, mb: 1 }} />
    <Typography variant="h6" gutterBottom>
      {tile.title}
    </Typography>
    <Typography variant="body2" color="textSecondary">
      {tile.description}
    </Typography>
    {tile.comingSoon && (
      <Chip label="Coming soon" size="small" sx={{ mt: 1.5 }} />
    )}
  </CardContent>
);

const AgentDashboard = () => {
  const navigate = useNavigate();
  const { user, signout } = useAuth();
  const name = user?.agent?.preferredName || user?.firstName || "";

  const handleSignOut = async () => {
    await signout();
    navigate("/login");
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            {name ? `Welcome, ${name}` : "Welcome"}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            What would you like to do?
          </Typography>
        </Box>
        <Button
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={handleSignOut}
          data-testid="dashboard-sign-out"
        >
          Sign out
        </Button>
      </Box>
      <Grid container spacing={2}>
        {TILES.map((tile) => (
          <Grid item xs={12} sm={6} md={4} key={tile.key}>
            <Card variant="outlined" sx={{ height: "100%", opacity: tile.comingSoon ? 0.6 : 1 }}>
              {tile.comingSoon ? (
                <TileBody tile={tile} />
              ) : (
                <CardActionArea
                  sx={{ height: "100%" }}
                  onClick={() => navigate(tile.to)}
                  data-testid={`tile-${tile.key}`}
                >
                  <TileBody tile={tile} />
                </CardActionArea>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AgentDashboard;
